import React, { useState, useContext } from 'react';
import {
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Link,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import AuthContext from '../../context/AuthContext';
import { INK, MUTED, RULE } from '../analytics/analyticsUi';

const PORTAL_POINTS = [
  'Connect your WhatsApp Business numbers and keep them in one place.',
  'See delivery rates and message categories for each number.',
  'Review your message templates and their approval status with Meta.'
];

const PASSWORD_MISMATCH = 'The passwords do not match. Enter the same password in both fields.';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    company: '',
    phone: ''
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { name, email, password, confirmPassword, company, phone } = formData;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate passwords match
    if (password !== confirmPassword) {
      setError(PASSWORD_MISMATCH);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const success = await register({
        name,
        email,
        password,
        company,
        phone
      });

      if (success) {
        // Redirect to dashboard only after registration is successful
        navigate('/dashboard');
      } else {
        setError('Your account was not created. Check your details and try again, or sign in if you already have an account.');
        setLoading(false);
      }
    } catch (err) {
      setError(err.response
        ? err.response.data?.error || 'Your account was not created. Try again in a moment.'
        : 'Could not reach the server. Check your connection and try again.');
      setLoading(false);
    }
  };

  const passwordAdornment = (
    <InputAdornment position="end">
      <IconButton
        aria-label={showPassword ? 'Hide passwords' : 'Show passwords'}
        aria-pressed={showPassword}
        onClick={() => setShowPassword((prev) => !prev)}
        onMouseDown={(e) => e.preventDefault()}
        edge="end"
      >
        {showPassword ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  const mismatch = error === PASSWORD_MISMATCH;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        px: { xs: 2, sm: 3 },
        py: { xs: 4, md: 6 }
      }}
    >
      <Paper
        variant="outlined"
        component="main"
        sx={{
          width: '100%',
          maxWidth: { xs: 480, md: 980 },
          borderColor: RULE,
          borderRadius: 2,
          overflow: 'hidden',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '7fr 5fr' }
        }}
      >
        <Box sx={{ p: { xs: 3, sm: 5 } }}>
          <Typography
            component="h1"
            sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' }, fontWeight: 700, letterSpacing: '-0.025em', color: INK }}
          >
            Create your account
          </Typography>
          <Typography variant="body2" sx={{ color: MUTED, mt: 0.75, mb: 3 }}>
            Tell us who you are and how to reach you. All fields are required.
          </Typography>

          {error && (
            <Alert severity="error" role="alert" sx={{ mb: 2.5, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                columnGap: 2,
                rowGap: 2
              }}
            >
              <TextField
                required
                fullWidth
                id="name"
                label="Full name"
                name="name"
                autoComplete="name"
                autoFocus
                value={name}
                onChange={handleChange}
              />
              <TextField
                required
                fullWidth
                id="company"
                label="Company name"
                name="company"
                autoComplete="organization"
                value={company}
                onChange={handleChange}
              />
              <TextField
                required
                fullWidth
                id="email"
                label="Email address"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={handleChange}
              />
              <TextField
                required
                fullWidth
                id="phone"
                label="Phone number"
                name="phone"
                type="tel"
                autoComplete="tel"
                value={phone}
                onChange={handleChange}
              />
              <TextField
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="new-password"
                value={password}
                onChange={handleChange}
                InputProps={{ endAdornment: passwordAdornment }}
              />
              <TextField
                required
                fullWidth
                name="confirmPassword"
                label="Confirm password"
                type={showPassword ? 'text' : 'password'}
                id="confirmPassword"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={handleChange}
                error={mismatch}
                helperText={mismatch ? 'Must match the password' : undefined}
              />
            </Box>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disableElevation
              disabled={loading}
              aria-busy={loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{ mt: 3, py: 1.25, textTransform: 'none', fontWeight: 600, fontSize: '1rem', borderRadius: 2 }}
            >
              {loading ? 'Creating account' : 'Create account'}
            </Button>
            <Typography variant="body2" sx={{ color: MUTED, mt: 3 }}>
              Already have an account?{' '}
              <Link component={RouterLink} to="/login" sx={{ color: '#0D7332', fontWeight: 600 }}>
                Sign in
              </Link>
            </Typography>
          </Box>
        </Box>

        <Box
          component="aside"
          aria-label="About the portal"
          sx={{
            p: { xs: 3, sm: 5 },
            bgcolor: '#F6F8FA',
            borderTop: { xs: `1px solid ${RULE}`, md: 'none' },
            borderLeft: { xs: 'none', md: `1px solid ${RULE}` },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}
        >
          <Typography component="h2" sx={{ fontSize: '1.05rem', fontWeight: 600, color: INK, mb: 0.5 }}>
            WhatsApp API Portal
          </Typography>
          <Typography variant="body2" sx={{ color: MUTED, mb: 2.5 }}>
            WhatsApp Business messaging, provided by Bitvoice.
          </Typography>
          <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
            {PORTAL_POINTS.map((point) => (
              <Box
                component="li"
                key={point}
                sx={{ display: 'flex', gap: 1.5, py: 1.25, borderTop: `1px solid ${RULE}` }}
              >
                <Box
                  aria-hidden="true"
                  sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: 'primary.main', flexShrink: 0, mt: '7px' }}
                />
                <Typography variant="body2" sx={{ color: INK }}>
                  {point}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default Register;
